import type { StaticEntityType } from "@config";
import { useCreateEntity } from "@editor/hooks";
import { tryInsertElementIntoActiveCell } from "@editor/modules/editor/@editor/element/options/table/hooks";
import { makeImageElementFromMediaFile } from "@editor/modules/media-lib/modules/images-panel/utils";
import { makeVideoElementFromMediaFile } from "@editor/modules/media-lib/modules/videos-panel/utils";
import type {
  IMediaLibDialogItem,
  IMediaLibDialogResponse,
  TElement,
} from "@editor/modules/media-lib/types";
import { makeLogoElementFromMediaFile } from "@editor/modules/media-lib/utils";
import { useAddElementStoreShallow } from "@editor/modules/menu/modules/elements-library/store";
import { getActiveEntityPayload } from "@providers/custom-dnd-provider/utils";
import type { IMediaFile } from "@/shared/types";
import { useMediaLibDialogStore } from "../stores/media-lib-dialog.store";
import { getMediaLibDialogMediaFileUrl } from "./get-media-lib-dialog-media-file-url";

interface IProps {
  insertElementOnSelect?: boolean;
  onSelect?: (items: IMediaLibDialogResponse) => void;
  onMultiSelect?: (items: IMediaLibDialogResponse[]) => void;
  onClose: () => void;
}

export const useMediaLibDialogSubmit = ({
  onSelect,
  insertElementOnSelect,
  onMultiSelect,
  onClose,
}: IProps) => {
  const setIsSubmitting = useMediaLibDialogStore((s) => s.setIsSubmitting);
  const { handleCreateEntity } = useCreateEntity();

  const { parentId } = useAddElementStoreShallow((s) => ({
    parentId: s.parentId,
  }));

  const handleUpload = async (items: IMediaLibDialogItem[]) => {
    const isUploading = useMediaLibDialogStore.getState().isSubmitting;
    if (isUploading) return;

    const promises: Promise<void>[] = [];
    const resultItems: IMediaLibDialogResponse[] = [];

    setIsSubmitting(true);
    for (const item of items) {
      if (item.mediaFile) {
        resultItems.push({
          id: item.id,
          type: item.type,
          mediaFile: item.mediaFile,
          mediaFrom: item.mediaFrom,
        });
      } else {
        resultItems.push({
          id: item.id,
          type: item.type,
          mediaFile: {
            url: getMediaLibDialogMediaFileUrl(item.url),
          } as IMediaFile,
          mediaFrom: item.mediaFrom,
        });
      }
    }

    await Promise.allSettled(promises);
    setIsSubmitting(false);
    return resultItems;
  };

  const handleInsertElement = async (file: IMediaLibDialogResponse) => {
    let element: TElement | null = null;

    if (file.type === "image") {
      element = makeImageElementFromMediaFile({
        mediaFile: file.mediaFile,
      });
    } else if (file.type === "logo") {
      element = makeLogoElementFromMediaFile({
        mediaFile: file.mediaFile,
      });
    } else if (file.type === "video") {
      element = makeVideoElementFromMediaFile({
        mediaFile: file.mediaFile,
      });
    }

    if (!element) return;

    // Tables 2.0 in-cell elements: with a single table cell selected, the
    // media pick inserts INTO that cell instead of below the table.
    if (!parentId && tryInsertElementIntoActiveCell(element)) return;

    if (parentId) {
      handleCreateEntity({
        payload: element.json,
        entity: {
          id: "",
          type: "",
          name: "",
          parentId: parentId as string,
        },
        direction: "replace",
        isPlaceholder: true,
      });
      return;
    }

    const entity = getActiveEntityPayload({
      element: element.json,
    });
    if (!entity) return;

    handleCreateEntity({
      payload: (entity.payload || element.json) as StaticEntityType,
      entity: {
        id: entity.id,
        type: entity.type,
        name: entity.name,
        parentId: entity.parentId,
      },
      direction: "bottom",
    });
  };

  const handleSelect = async (item: IMediaLibDialogItem) => {
    const isUploading = useMediaLibDialogStore.getState().isSubmitting;
    if (isUploading || (!onSelect && !insertElementOnSelect)) return;

    const resp = await handleUpload([item]);

    if (resp?.[0]) {
      if (insertElementOnSelect) {
        handleInsertElement(resp[0]);
      } else if (onSelect) {
        onSelect(resp[0]);
      }
    }
    onClose();
  };

  const handleMultiSelect = async (items: IMediaLibDialogItem[]) => {
    const isUploading = useMediaLibDialogStore.getState().isSubmitting;
    if (!onMultiSelect || isUploading) return;

    const resp = await handleUpload(items);
    if (resp) {
      onMultiSelect(resp);
    }
    onClose();
  };

  return {
    handleSelect,
    handleMultiSelect,
  };
};
